'use client';

import { PeValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: PeValues;
  onValuesChange: (field: keyof PeValues, value: number | string) => void;
  translations: ToolsTranslations['pe'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof PeValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof PeValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">
      {/* Price per Share */}
      <div className="space-y-3">
        <label htmlFor="price-per-share" className="block text-sm font-medium text-gray-700">
          {translations.pricePerShare}
        </label>
        <div className="relative">
          <input
            id="price-per-share"
            type="number"
            min="0"
            step="0.01"
            value={values.pricePerShare}
            onChange={(e) => handleInputChange('pricePerShare', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {translations.currencySuffix}
          </span>
        </div>
        
        {/* Price per Share Slider */}
        <div className="relative">
          <input
            id="price-per-share-slider"
            type="range"
            min="1"
            max="1000"
            step="1"
            value={values.pricePerShare}
            onChange={(e) => handleSliderChange('pricePerShare', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.pricePerShare} slider`}
          />
          <div className="h-2 bg-gray-200 rounded-lg">
            <div 
              className="h-2 bg-blue-500 rounded-lg transition-all duration-150 ease-out"
              style={{ width: `${Math.min((values.pricePerShare / 1000) * 100, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.currencySuffix}</span>
          <span>1000 {translations.currencySuffix}</span>
        </div>
      </div>

      {/* Earnings per Share */}
      <div className="space-y-3">
        <label htmlFor="earnings-per-share" className="block text-sm font-medium text-gray-700">
          {translations.earningsPerShare}
        </label>
        <div className="relative">
          <input
            id="earnings-per-share"
            type="number"
            min="0"
            step="0.01"
            value={values.earningsPerShare}
            onChange={(e) => handleInputChange('earningsPerShare', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {translations.currencySuffix}
          </span>
        </div>
        
        {/* Earnings per Share Slider */}
        <div className="relative">
          <input
            id="earnings-per-share-slider"
            type="range"
            min="1"
            max="100"
            step="1"
            value={values.earningsPerShare}
            onChange={(e) => handleSliderChange('earningsPerShare', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.earningsPerShare} slider`}
          />
          <div className="h-2 bg-gray-200 rounded-lg">
            <div 
              className="h-2 bg-blue-500 rounded-lg transition-all duration-150 ease-out"
              style={{ width: `${Math.min((values.earningsPerShare / 100) * 100, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.currencySuffix}</span>
          <span>100 {translations.currencySuffix}</span>
        </div>
      </div>

      {/* Currency Selector */}
      <div className="space-y-3">
        <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
          {translations.currencyLabel}
        </label>
        <select
          id="currency"
          value={values.currency}
          onChange={(e) => handleInputChange('currency', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="kr">Svenska kronor (kr)</option>
          <option value="usd">US Dollar ($)</option>
          <option value="eur">Euro (€)</option>
          <option value="nok">Norska kronor (kr)</option>
        </select>
      </div>
    </div>
  );
}
